/** Phase 6: enterprise Operations Dashboard (Datadog/Grafana-inspired).
 *  Polls /api/ops/dashboard every 3s (backend caches DB sections for 10s,
 *  so overhead stays negligible). Rendered at the bottom of the Admin
 *  Console beside the Reset Event panel.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AreaChart, Area, ResponsiveContainer, Tooltip, YAxis } from "recharts";
import api from "../lib/api";
import { T } from "../lib/i18n";
import {
  Activity, Gauge, Wifi, Radio, Database, AlertTriangle, ListChecks,
  ShieldCheck, ShieldAlert, ShieldX, Bug, BellRing, Send,
} from "lucide-react";

const H_STYLES = {
  healthy: { Icon: ShieldCheck, cls: "bg-emerald-500/10 border-emerald-500/40 text-emerald-500", label: "Healthy" },
  warning: { Icon: ShieldAlert, cls: "bg-amber-500/10 border-amber-500/40 text-amber-500", label: "Warning" },
  critical: { Icon: ShieldX, cls: "bg-red-500/10 border-red-500/40 text-red-500", label: "Critical" },
};
const STATE_DOT = { green: "bg-emerald-500", yellow: "bg-amber-400", red: "bg-red-500" };
const ALERT_CLS = {
  critical: "border-red-500/40 bg-red-500/10 text-red-600",
  warning: "border-amber-500/40 bg-amber-500/10 text-amber-600",
  info: "border-sky-500/40 bg-sky-500/10 text-sky-600",
};

function Stat({ label, value, suffix, warn }) {
  return (
    <div className="text-center px-2 py-2">
      <div className={`font-display text-2xl tabular-nums ${warn ? "text-red-500" : "text-motif"}`} data-testid={`opsx-${label.replace(/\W+/g, "-").toLowerCase()}`}>
        {value}{suffix && <span className="text-xs text-motif-muted ml-0.5">{suffix}</span>}
      </div>
      <div className="text-[10px] uppercase tracking-wide text-motif-muted mt-0.5">{label}</div>
    </div>
  );
}

function Block({ icon: Icon, title, children, tid }) {
  return (
    <div className="rounded-2xl border border-motif bg-accent-motif/30 p-4" data-testid={tid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-3.5 h-3.5 text-primary-motif" />
        <div className="text-[11px] font-semibold uppercase tracking-wider text-motif-muted">{title}</div>
      </div>
      {children}
    </div>
  );
}

function MiniChart({ data, dataKey, color, label, fmt }) {
  return (
    <div className="rounded-xl border border-motif p-3">
      <div className="text-[10px] uppercase tracking-wide text-motif-muted mb-1">{label}</div>
      <div style={{ width: "100%", height: 64 }}>
        <ResponsiveContainer>
          <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={`g-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <YAxis hide domain={[0, "auto"]} />
            <Tooltip contentStyle={{ fontSize: 11, padding: "4px 8px", borderRadius: 8 }} labelFormatter={() => ""} formatter={(v) => [fmt ? fmt(v) : v, label]} />
            <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={1.5} fill={`url(#g-${dataKey})`} isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function OperationsDashboard() {
  const [d, setD] = useState(null);
  const [siteOk, setSiteOk] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/ops/dashboard").then(({ data }) => alive && setD(data)).catch(() => {});
    load();
    const t = setInterval(load, 3000);
    // Public-site reachability: one lightweight client-side check per mount.
    fetch("/", { method: "HEAD" }).then((r) => alive && setSiteOk(r.ok)).catch(() => alive && setSiteOk(false));
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!d) {
    return (
      <div className="card-elev p-6" data-testid="ops-dashboard">
        <div className="text-xs text-motif-muted">Loading operations dashboard…</div>
      </div>
    );
  }

  const h = H_STYLES[d.health.status] || H_STYLES.healthy;
  const HIcon = h.Icon;
  const history = (d.history || []).map((s) => ({ ...s, t: s.ts }));
  const readiness = [
    ...d.readiness,
    { id: "public_site", label: "Public Site Reachable", link: "/",
      state: siteOk === null ? "yellow" : siteOk ? "green" : "red",
      detail: siteOk === null ? "checking…" : siteOk ? "venuefy marketing site online" : "unreachable" },
  ];
  const la = d.live_activity;
  const f = d.feature;
  const dbv = d.database;

  return (
    <div className="card-elev p-5" data-testid="ops-dashboard">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60"></span><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span></span>
        <div className="font-display text-2xl">{T("Operations")}</div>
        <div className={`ml-2 inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-sm font-semibold ${h.cls}`} data-testid="ops-health">
          <HIcon className="w-4 h-4" /> {h.label}
          {d.health.reasons.length > 0 && <span className="font-normal opacity-80">· {d.health.reasons.join(", ")}</span>}
        </div>
        <div className="text-[10px] text-motif-muted ml-auto">refreshes every 3s · uptime {Math.floor(d.uptime_s / 3600)}h {Math.floor((d.uptime_s % 3600) / 60)}m</div>
      </div>

      {/* Alerts */}
      {d.alerts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mb-4" data-testid="ops-alerts">
          {d.alerts.map((a, i) => (
            <div key={`${a.title}-${i}`} className={`rounded-xl border px-4 py-3 ${ALERT_CLS[a.level] || ALERT_CLS.info}`}>
              <div className="flex items-center gap-2 text-sm font-semibold"><AlertTriangle className="w-4 h-4" /> {a.title}</div>
              <div className="text-xs opacity-80 mt-0.5">{a.detail}</div>
            </div>
          ))}
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4" data-testid="ops-charts">
        <MiniChart data={history} dataKey="rpm" color="#6366f1" label="Requests / min (15m)" />
        <MiniChart data={history} dataKey="avg_ms" color="#0ea5e9" label="Avg latency ms (15m)" />
        <MiniChart data={history} dataKey="errors" color="#ef4444" label="Errors (15m)" />
        <MiniChart data={history} dataKey="ws" color="#10b981" label="WebSockets (15m)" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Performance */}
        <Block icon={Gauge} title="Performance" tid="ops-performance">
          <div className="grid grid-cols-4 gap-1">
            <Stat label="Req / min" value={d.perf.req_last_60s} />
            <Stat label="Avg" value={d.perf.avg_ms} suffix="ms" warn={d.perf.avg_ms > 400} />
            <Stat label="P95" value={d.perf.p95_ms} suffix="ms" warn={d.perf.p95_ms > 750} />
            <Stat label="P99" value={d.perf.p99_ms} suffix="ms" warn={d.perf.p99_ms > 1500} />
            <Stat label="Peak 15m" value={d.perf.peak_ms_15m} suffix="ms" />
            <Stat label="Error %" value={d.perf.error_pct} suffix="%" warn={d.perf.error_pct > 1} />
            <Stat label="Success %" value={d.perf.success_pct} suffix="%" />
            <Stat label="Total req" value={d.perf.total_requests} />
          </div>
        </Block>

        {/* WebSockets */}
        <Block icon={Wifi} title="WebSockets" tid="ops-websockets">
          <div className="grid grid-cols-4 gap-1">
            <Stat label="Connected" value={d.ws.total} />
            <Stat label="Rooms" value={d.ws.rooms} />
            <Stat label="Connects" value={d.ws.connects} />
            <Stat label="Drops" value={d.ws.drops} />
          </div>
          <div className="text-[10px] text-motif-muted mt-2">Connected = live sockets now (guests + staff + surfaces). Connects/drops since server start.</div>
        </Block>

        {/* Live Activity */}
        <Block icon={Radio} title="Live Activity — last 60s (from real API traffic)" tid="ops-live-activity">
          <div className="grid grid-cols-3 gap-1">
            <Stat label="Viewing Menu" value={la.menu} />
            <Stat label="Venue Map" value={la.venue_map} />
            <Stat label="Viewing Feed" value={la.feed} />
            <Stat label="Projector" value={la.projector} />
            <Stat label="Crossword" value={la.crossword} />
            <Stat label="Who Will" value={la.whowill} />
            <Stat label="Requests" value={la.requests} />
            <Stat label="Uploads" value={la.uploads} />
            <Stat label="Idle Guests" value={la.idle} />
          </div>
        </Block>

        {/* Feature Activity */}
        <Block icon={Activity} title="Feature Activity — today" tid="ops-feature-activity">
          <div className="grid grid-cols-4 gap-1">
            <Stat label="Feed Posts" value={f.feed_posts} />
            <Stat label="Comments" value={f.comments} />
            <Stat label="Likes" value={f.likes} />
            <Stat label="Uploads" value={f.uploads} />
            <Stat label="Embroidery" value={f.embroidery_orders} />
            <Stat label="Kitchen" value={f.kitchen_requests} />
            <Stat label="Drinks" value={f.drink_requests} />
            <Stat label="Music" value={f.music_requests} />
            <Stat label="Help" value={f.help_requests} />
            <Stat label="Announce" value={f.announcements} />
            <Stat label="WW Votes" value={f.whowill_votes} />
            <Stat label="Crossword" value={f.crossword_entries} />
          </div>
        </Block>

        {/* Database */}
        <Block icon={Database} title="Database" tid="ops-database">
          <div className="grid grid-cols-3 gap-1">
            <Stat label="Guests" value={dbv.guest_count} />
            <Stat label="Inventory Left" value={dbv.inventory_remaining} />
            <Stat label="Open Req" value={dbv.open_requests} />
            <Stat label="Done Req" value={dbv.completed_requests} />
            <Stat label="Crossword" value={dbv.crossword_entries} />
            <Stat label="WW Questions" value={dbv.whowill_questions} />
          </div>
          <div className="text-[11px] text-motif-muted mt-2 text-center">
            {dbv.event_name} · {dbv.event_date} · queue {dbv.queue_open ? "open" : "paused"}{typeof d.disk_pct === "number" ? ` · disk ${d.disk_pct}%` : ""}
          </div>
        </Block>

        {/* Recent Errors */}
        <Block icon={Bug} title="Recent Errors (latest 20)" tid="ops-recent-errors">
          {d.recent_errors.length === 0 ? (
            <div className="text-xs text-motif-muted py-3 text-center">{T("No backend exceptions, failed uploads, or failed API calls recorded.")}</div>
          ) : (
            <div className="max-h-40 overflow-y-auto space-y-1">
              {d.recent_errors.map((e, i) => (
                <div key={i} className="flex items-center gap-2 text-xs rounded-lg bg-red-500/5 border border-red-500/20 px-2.5 py-1.5">
                  <span className="font-mono text-red-500">{e.status}</span>
                  <span className="truncate flex-1">{e.path}</span>
                  <span className="text-motif-muted">{e.kind === "failed_upload" ? "upload" : "5xx"} · {e.ago_s < 60 ? `${e.ago_s}s` : `${Math.floor(e.ago_s / 60)}m`} ago</span>
                </div>
              ))}
            </div>
          )}
          <div className="text-[10px] text-motif-muted mt-2">Frontend errors are not captured server-side yet.</div>
        </Block>
      </div>

      {/* Push Service (Phase 5D) */}
      <PushServicePanel />

      {/* Event Readiness */}
      <div className="rounded-2xl border border-motif bg-accent-motif/30 p-4 mt-3" data-testid="ops-readiness">
        <div className="flex items-center gap-2 mb-3">
          <ListChecks className="w-3.5 h-3.5 text-primary-motif" />
          <div className="text-[11px] font-semibold uppercase tracking-wider text-motif-muted">{T("Event Readiness")}</div>
          <div className="text-[10px] text-motif-muted ml-auto">
            {readiness.filter((r) => r.state === "green").length}/{readiness.length} ready
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
          {readiness.map((r) => (
            <Link key={r.id} to={r.link} className="flex items-center gap-2.5 rounded-lg border border-motif px-3 py-2 hover:bg-accent-motif transition" data-testid={`ops-ready-${r.id}`}>
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${STATE_DOT[r.state] || STATE_DOT.yellow}`} />
              <span className="text-xs text-motif flex-1 truncate">{r.label}</span>
              <span className="text-[10px] text-motif-muted truncate max-w-[40%]">{r.detail}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

// Phase 5D: Web Push health + safe test-send controls
const DEV_TEST_NAMES = ["John Smith", "Jane Doe", "Bruce Wayne", "Clark Kent"];

function PushServicePanel() {
  const [h, setH] = useState(null);
  const [devName, setDevName] = useState(DEV_TEST_NAMES[0]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState("");

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/push/health").then(({ data }) => alive && setH(data)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const sendTest = async (payload) => {
    if (sending) return;
    setSending(true);
    setResult("");
    try {
      const { data } = await api.post("/push/test", payload);
      if (data.reason === "no_subscriptions") setResult("No subscription for that target — enable alerts on the device first.");
      else if (data.reason) setResult(`Not sent: ${data.reason}`);
      else setResult(`Sent: ${data.success || 0} ok, ${data.failed || 0} failed, ${data.expired || 0} expired`);
    } catch (e) {
      setResult(e?.response?.data?.detail || "Test push failed");
    } finally { setSending(false); }
  };

  const fmtTime = (iso) => {
    if (!iso) return "never";
    try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); }
    catch { return iso; }
  };

  if (!h) return null;
  return (
    <div className="rounded-2xl border border-motif bg-accent-motif/30 p-4 mt-3" data-testid="ops-push-service">
      <div className="flex items-center gap-2 mb-3">
        <BellRing className="w-3.5 h-3.5 text-primary-motif" />
        <div className="text-[11px] font-semibold uppercase tracking-wider text-motif-muted">{T("Push Service")}</div>
        <span
          className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${h.config?.configured ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600" : "border-amber-500/40 bg-amber-500/10 text-amber-600"}`}
          data-testid="ops-push-vapid"
        >
          VAPID {h.config?.configured ? "configured" : "missing"}
        </span>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-1 mb-3">
        <Stat label="Active subs" value={h.active_subscriptions} />
        <Stat label="Guests subscribed" value={h.guests_subscribed} />
        <Stat label="Sent today" value={h.today_success} />
        <Stat label="Failed today" value={h.today_failed} warn={h.today_failed > 0} />
        <Stat label="Expired" value={h.expired_removed} />
        <div className="text-center px-2 py-2">
          <div className="font-display text-2xl tabular-nums text-motif" data-testid="opsx-last-push">{fmtTime(h.last_attempt?.at)}</div>
          <div className="text-[10px] uppercase tracking-wide text-motif-muted mt-0.5">Last push</div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => sendTest({ target: "self" })}
          disabled={sending}
          className="btn-primary text-[11px] px-3 py-1.5 flex items-center gap-1.5"
          data-testid="ops-push-test-self"
        >
          <Send className="w-3 h-3" /> {sending ? "Sending…" : "Send test push to me"}
        </button>
        <div className="flex items-center gap-1.5">
          <select
            value={devName}
            onChange={(e) => setDevName(e.target.value)}
            className="input-motif text-[11px] py-1.5 px-2 w-32"
            data-testid="ops-push-dev-select"
          >
            {DEV_TEST_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <button
            onClick={() => sendTest({ target: "dev", name: devName })}
            disabled={sending}
            className="btn-ghost text-[11px] px-3 py-1.5"
            data-testid="ops-push-test-dev"
          >
            Test dev account
          </button>
        </div>
        {result && <span className="text-[10px] text-motif-muted" data-testid="ops-push-test-result">{result}</span>}
      </div>
    </div>
  );
}
